import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSave, MdGroups, MdSearch, MdCheckCircle } from 'react-icons/md';
import { gruposAPI, clientesAPI } from '../services/api';
import { useModal } from '../context/ModalContext';

const Container = styled.div`
  max-width: 900px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const BackButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.textPrimary};
  cursor: pointer;

  svg { font-size: 1.5rem; }
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
`;

const FormCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const FormSection = styled.div`
  margin-bottom: ${p => p.theme.spacing.xl};
  &:last-child { margin-bottom: 0; }
`;

const SectionTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.xl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing.sm} 0;
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};

  svg { color: ${p => p.theme.colors.primary}; }
`;

const SectionDescription = styled.p`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
  margin: 0 0 ${p => p.theme.spacing.lg} 0;
`;

const Label = styled.label`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  font-weight: 600;
  display: block;
  margin-bottom: ${p => p.theme.spacing.sm};

  span.required { color: ${p => p.theme.colors.danger}; }
`;

const Input = styled.input`
  width: 100%;
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  box-sizing: border-box;

  &:focus { border-color: ${p => p.theme.colors.primary}; outline: none; }
`;

const SearchWrapper = styled.div`
  position: relative;
  margin-bottom: ${p => p.theme.spacing.md};
`;

const SearchInput = styled(Input)`
  padding-left: 2.5rem;
`;

const SearchIcon = styled(MdSearch)`
  position: absolute;
  left: ${p => p.theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${p => p.theme.colors.textLight};
  font-size: 1.25rem;
`;

const ClientListBox = styled.div`
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
`;

const ClientRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
  background: ${p => p.selected ? `${p.theme.colors.primary}0d` : 'transparent'};

  &:last-child { border-bottom: none; }
  &:hover { background: ${p => p.selected ? `${p.theme.colors.primary}15` : p.theme.colors.background}; }
`;

const CheckCircle = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.5px solid ${p => p.selected ? p.theme.colors.primary : p.theme.colors.border};
  background: ${p => p.selected ? p.theme.colors.primary : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
  font-size: 0.9rem;
`;

const ClientRowName = styled.div`
  flex: 1;
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
`;

const ClientRowMeta = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
`;

const EmptyResult = styled.div`
  padding: 24px;
  text-align: center;
  color: ${p => p.theme.colors.textLight};
  font-size: ${p => p.theme.fontSizes.sm};
`;

const SelectedSummary = styled.div`
  margin-top: ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${p => p.theme.spacing.xl};
  padding-top: ${p => p.theme.spacing.xl};
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const Button = styled.button`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.xl};
  background-color: ${p => p.primary ? p.theme.colors.primary : p.theme.colors.backgroundLight};
  color: ${p => p.primary ? 'white' : p.theme.colors.textPrimary};
  border: 1px solid ${p => p.primary ? p.theme.colors.primary : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { transform: translateY(-1px); }
`;

const NuevoGrupo = () => {
  const navigate = useNavigate();
  const { toast } = useModal();

  const [nombre, setNombre] = useState('');
  const [clientes, setClientes] = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [search, setSearch] = useState('');
  const [seleccionados, setSeleccionados] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    clientesAPI.getAll()
      .then(r => setClientes(r.data.data || []))
      .catch(() => toast('Error al cargar clientes', 'error'))
      .finally(() => setLoadingClientes(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtrados = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return clientes;
    return clientes.filter(c =>
      c.nombre.toLowerCase().includes(term) ||
      (c.email && c.email.toLowerCase().includes(term)) ||
      (c.telefono && c.telefono.includes(term))
    );
  }, [clientes, search]);

  const toggleSeleccionado = (clienteId) => {
    setSeleccionados(prev =>
      prev.includes(clienteId) ? prev.filter(id => id !== clienteId) : [...prev, clienteId]
    );
  };

  const handleGuardar = async () => {
    if (!nombre.trim()) {
      toast('Ingresa un nombre para el grupo', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await gruposAPI.create({ nombre: nombre.trim() });
      const grupoId = res.data.data.id;

      let erroresAsignacion = 0;
      for (const clienteId of seleccionados) {
        try {
          await clientesAPI.update(clienteId, { grupo_id: grupoId });
        } catch {
          erroresAsignacion += 1;
        }
      }

      if (erroresAsignacion > 0) {
        toast(`Grupo creado, pero ${erroresAsignacion} cliente(s) no se pudieron agregar`, 'warning');
      } else {
        toast('Grupo creado exitosamente', 'success');
      }
      navigate(`/grupos/${grupoId}`);
    } catch (err) {
      toast('Error al crear el grupo: ' + (err.message || 'Error desconocido'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <PageHeader>
        <BackButton onClick={() => navigate('/grupos')}>
          <MdArrowBack />
        </BackButton>
        <Title>Nuevo Grupo de Clientes</Title>
      </PageHeader>

      <FormCard>
        <FormSection>
          <SectionTitle><MdGroups /> Datos del Grupo</SectionTitle>
          <SectionDescription>Nombre con el que identificarás a este grupo (ej. Familia Pérez, Corporativo ACME).</SectionDescription>
          <Label>Nombre del grupo <span className="required">*</span></Label>
          <Input
            type="text"
            value={nombre}
            onChange={e => setNombre(e.target.value)}
            placeholder="Ej. Familia Pérez"
            autoFocus
          />
        </FormSection>

        <FormSection>
          <SectionTitle><MdCheckCircle /> Clientes del Grupo</SectionTitle>
          <SectionDescription>
            Busca y selecciona los clientes que formarán parte de este grupo.
          </SectionDescription>

          <SearchWrapper>
            <SearchIcon />
            <SearchInput
              type="text"
              placeholder="Buscar cliente por nombre, email o teléfono..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </SearchWrapper>

          <ClientListBox>
            {loadingClientes ? (
              <EmptyResult>Cargando clientes...</EmptyResult>
            ) : filtrados.length === 0 ? (
              <EmptyResult>Sin resultados para "{search}"</EmptyResult>
            ) : (
              filtrados.map(c => {
                const selected = seleccionados.includes(c.id);
                return (
                  <ClientRow key={c.id} selected={selected} onClick={() => toggleSeleccionado(c.id)}>
                    <CheckCircle selected={selected}>{selected && <MdCheckCircle />}</CheckCircle>
                    <div style={{ flex: 1 }}>
                      <ClientRowName>{c.nombre}</ClientRowName>
                      {(c.email || c.telefono) && (
                        <ClientRowMeta>{c.telefono || c.email}</ClientRowMeta>
                      )}
                    </div>
                  </ClientRow>
                );
              })
            )}
          </ClientListBox>

          <SelectedSummary>
            {seleccionados.length} cliente{seleccionados.length === 1 ? '' : 's'} seleccionado{seleccionados.length === 1 ? '' : 's'}
          </SelectedSummary>
        </FormSection>

        <ButtonGroup>
          <Button type="button" onClick={() => navigate('/grupos')} disabled={saving}>
            Cancelar
          </Button>
          <Button primary onClick={handleGuardar} disabled={saving || !nombre.trim()}>
            <MdSave />
            {saving ? 'Guardando...' : 'Crear Grupo'}
          </Button>
        </ButtonGroup>
      </FormCard>
    </Container>
  );
};

export default NuevoGrupo;
